'use client';

import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import { useState } from 'react';

import { useAppCopy } from '@/i18n/use-app-copy';

import { FounderAvatar } from './founder-avatar';
import { SUPPORT_WIDGET_LAUNCHER_STYLE } from './support-widget-geometry';

const SupportWidget = dynamic(
  () => import('./support-widget').then((module) => module.SupportWidget),
  { ssr: false }
);

/**
 * Keep the support client, polling loop, thread UI, and textarea code outside
 * the critical route bundle. The lightweight launcher is enough until a user
 * asks for help. On mobile dashboard routes, Support remains available in the
 * main navigation without covering page actions.
 */
export function LazySupportWidget({ mode }: { mode: 'in_app' | 'public' }) {
  const pathname = usePathname();
  const [activated, setActivated] = useState(false);
  const t = useAppCopy();

  const hiddenHere =
    mode === 'public' && /^\/(dashboard|admin|sign-in|sign-up)(\/|$)/.test(pathname ?? '');

  if (hiddenHere) return null;

  if (activated) return <SupportWidget mode={mode} initialOpen />;

  return (
    <button
      type="button"
      onClick={() => setActivated(true)}
      // Public mode sits under the marketing provider (no `app` catalog): keep it English.
      aria-label={mode === 'in_app' ? t('support.widget.openSupportAria') : 'Open support chat'}
      style={SUPPORT_WIDGET_LAUNCHER_STYLE}
      className={
        mode === 'in_app'
          ? 'fixed bottom-3 end-3 z-[60] hidden h-12 w-12 items-center justify-center rounded-full border border-line-strong bg-zinc-900 p-1 shadow-xl transition-transform hover:scale-105 sm:flex'
          : 'fixed bottom-3 end-3 z-[60] flex h-12 w-12 items-center justify-center rounded-full border border-line-strong bg-zinc-900 p-1 shadow-xl transition-transform hover:scale-105'
      }
    >
      <FounderAvatar size={46} name="Tosh" avatar={null} className="max-h-full max-w-full" />
    </button>
  );
}
